import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  GitBranch,
  Bell,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronRight,
  FileText,
  MessageCircle,
  TrendingUp,
  Zap,
  Star,
  Calendar,
  Crown,
  Bot,
  Lock,
  Sparkles,
  HelpCircle,
  DollarSign,
  X,
  BookOpen,
  Building2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import CaminoAlExito from "../components/CaminoAlExito";
import { PlanBadge, PlanIndicator, PremiumFeatureCard } from "../components/PlanBadge";
import { ServiceLevelBadge } from "../components/ServiceLevelBadge";
import RiskAnalysisCard from "../components/RiskAnalysisCard";
import ApprovalBoostCard from "../components/ApprovalBoostCard";
import Sidebar from "../components/Sidebar";
import PendingDocumentsModal from "../components/PendingDocumentsModal";
import ChatButton from "../components/ChatButton";
import { NextStepBanner } from "../components/ClientJourneyGuide";
import UpcomingMeetingBanner from "../components/UpcomingMeetingBanner";
import ConsulateDirectory from "../components/Client/ConsulateDirectory";
import { WhatsAppSupportButton } from "../components/WhatsAppButtons";
import ServiceCompletedDashboard from "../components/Client/ServiceCompletedDashboard";
import PlanSection from "../components/Client/PlanSection";
import WhatsAppBubble from "../components/WhatsAppBubble";
import ClientOnboardingModal from "../components/ClientOnboardingModal";
import FamilyMembersManager from "../components/FamilyMembersManager";
import { DS160AuthorizationStatus } from "../components/DS160Authorization";
import QuickStartGuide from "../components/Client/QuickStartGuide";
import InterviewCountdown from "../components/Client/InterviewCountdown";
import { useMotivationalMessages, ProgressMotivation } from "../components/Client/MotivationalMessages";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, isPremium, isVip, hasFeature, getPlanName } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [prevCaseData, setPrevCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showPendingDocs, setShowPendingDocs] = useState(false);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [showAgentBanner, setShowAgentBanner] = useState(true);
  const [showChecklist, setShowChecklist] = useState(true);
  const [ds160Progress, setDs160Progress] = useState(0);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Hook para mensajes motivacionales
  useMotivationalMessages(caseData, prevCaseData);

  // Acuity link for agent recruitment
  const ACUITY_AGENT_LINK = "https://clubviajemos.as.me/?appointmentType=90007142";

  // IMMEDIATE redirect for VIP Partners (don't wait for caseData)
  useEffect(() => {
    if (user?.is_vip_partner) {
      navigate("/mis-referidos", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    // Don't fetch data if user is VIP Partner (will be redirected)
    if (user?.is_vip_partner) return;
    
    fetchDashboardData();
    checkPendingDocuments();
    fetchDs160Progress();
    fetchDocumentsCount();
    checkOnboardingStatus();
  }, [user]);

  const checkOnboardingStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/onboarding-status`);
      if (res.data.show_onboarding) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    }
  };

  const fetchDocumentsCount = async () => {
    try {
      const res = await axios.get(`${API}/documents/my-documents`);
      setDocumentsCount(res.data?.length || 0);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const checkPendingDocuments = async () => {
    try {
      const res = await axios.get(`${API}/document-requests/pending`);
      const count = res.data?.length || 0;
      setPendingDocsCount(count);
      // Show modal automatically if there are pending docs
      if (count > 0) {
        setShowPendingDocs(true);
      }
    } catch (error) {
      console.error("Error checking pending documents:", error);
    }
  };

  const fetchDs160Progress = async () => {
    try {
      const res = await axios.get(`${API}/ds160`);
      if (res.data) {
        // Calculate progress based on filled fields
        const totalFields = 50; // Approximate number of important fields
        let filledFields = 0;
        const data = res.data;
        
        // Count filled fields in each section
        if (data.personal_info?.surnames) filledFields += 5;
        if (data.personal_info?.given_names) filledFields += 5;
        if (data.travel_info?.purpose) filledFields += 5;
        if (data.travel_info?.intended_arrival_date) filledFields += 5;
        if (data.us_contact?.name) filledFields += 5;
        if (data.family_info?.father_name) filledFields += 5;
        if (data.work_education?.occupation) filledFields += 5;
        if (data.security_questions?.answered) filledFields += 10;
        
        const progress = Math.min(Math.round((filledFields / totalFields) * 100), 100);
        setDs160Progress(progress);
      }
    } catch (error) {
      console.error("Error fetching DS-160 progress:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [caseRes, notifRes] = await Promise.all([
        axios.get(`${API}/cases/my-case`).catch(err => {
          console.warn("No case found:", err);
          return { data: null };
        }),
        axios.get(`${API}/notifications`).catch(err => {
          console.warn("Notifications error:", err);
          return { data: [] };
        })
      ]);
      // Guardar datos previos para detectar cambios (mensajes motivacionales)
      setPrevCaseData(caseData);
      setCaseData(caseRes.data);
      setNotifications(notifRes.data || []);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setError("Error al cargar el dashboard. Por favor recarga la página.");
      // No dejar la pantalla en blanco - mostrar estado sin caso
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  };

  const createCase = async () => {
    try {
      await axios.post(`${API}/cases`, { visa_type: "B1/B2" });
      toast.success("¡Tu Camino al Éxito ha comenzado!");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear el caso");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleStepClick = (step) => {
    // Rutas correctas según VISA_STEPS del backend (6 pasos del Camino al Éxito):
    // 1: Cuenta Creada (no navega)
    // 2: Subir Documentos
    // 3: Llenar Cuestionario
    // 4: Verificación
    // 5: DS-160 Oficial
    // 6: ¡Entrevista!
    const routes = {
      2: "/step/documents",
      3: "/step/ds160",
      4: "/step/verification",
      5: "/step/ds160-oficial",
      6: "/interview-questions"  // Preparación para entrevista
    };
    if (routes[step.step_order]) {
      navigate(routes[step.step_order]);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getRiskBadge = (level) => {
    const badges = {
      low: { class: "badge-success", text: "Riesgo Bajo", icon: CheckCircle2 },
      medium: { class: "badge-warning", text: "Riesgo Medio", icon: AlertCircle },
      high: { class: "badge-danger", text: "Riesgo Alto", icon: AlertCircle },
      pending: { class: "badge-info", text: "Pendiente", icon: Clock },
      none: { class: "text-navy-400 bg-navy-100 px-2 py-1 rounded text-sm", text: "Sin datos", icon: Clock }
    };
    return badges[level] || badges.none;
  };

  const getStepRoute = (stepOrder) => {
    const routes = {
      2: "/step/documents",
      3: "/step/ds160",
      4: "/step/verification",
      5: "/step/ds160-oficial"
    };
    return routes[stepOrder] || "/dashboard";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  // Error state - show friendly message
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <div className="card-elevated p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-navy-900 mb-2">Algo salió mal</h2>
          <p className="text-navy-600 mb-4">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Recargar página
          </Button>
        </div>
      </div>
    );
  }

  // If VIP Partner, show loading while redirecting
  if (user?.is_vip_partner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-navy-600">Redirigiendo a tu Panel de Referidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-50">
      {/* Pending Documents Modal */}
      <PendingDocumentsModal 
        isOpen={showPendingDocs} 
        onClose={() => setShowPendingDocs(false)} 
      />

      {/* Client Onboarding Modal */}
      <ClientOnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        userName={user?.name?.split(' ')[0] || 'Cliente'}
      />

      {/* Sidebar */}
      <Sidebar />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white/80 backdrop-blur-xl border-b border-navy-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 pl-12">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <GitBranch className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-navy-900 hidden sm:block">Divídelo</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/notifications" className="relative p-2 hover:bg-navy-100 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-navy-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Link>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-navy-50 rounded-full">
              <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xs">{user?.name?.[0]}</span>
              </div>
              <span className="text-sm text-navy-700 font-medium">{user?.name?.split(' ')[0]}</span>
              <PlanBadge planType={user?.plan_type} size="small" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:pl-72">
        <div className="max-w-7xl mx-auto">
          {/* Service Plan Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 rounded-xl p-4 flex items-center justify-between ${
              user?.plan_type === "vip" 
                ? "bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300" 
                : user?.plan_type === "premium"
                ? "bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-300"
                : "bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300"
            }`}
            data-testid="service-plan-banner"
          >
            <div className="flex items-center gap-3">
              <ServiceLevelBadge planType={user?.plan_type || "estandar"} size="lg" />
              <span className="text-sm text-navy-600">
                {user?.plan_type === "vip" 
                  ? "Disfruta de atención prioritaria y proceso acelerado"
                  : user?.plan_type === "premium"
                  ? "Acceso a simulador de entrevista y revisión de documentos"
                  : "Plan básico de asesoría migratoria"}
              </span>
            </div>
            {user?.plan_type === "estandar" && (
              <a 
                href={`https://wa.me/19548059212?text=${encodeURIComponent("Hola, quiero mejorar mi plan")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
              >
                Mejorar plan →
              </a>
            )}
          </motion.div>

          {!caseData || !caseData.id ? (
            // No Case - Create Case CTA
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-elevated p-8 sm:p-12 text-center max-w-2xl mx-auto"
            >
              <div className="w-20 h-20 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <GitBranch className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 mb-4">
                ¡Bienvenido a Divídelo, {user?.name}!
              </h1>
              <p className="text-navy-600 text-lg mb-8 max-w-md mx-auto">
                Estás a un paso de estructurar tu proceso de visa B1/B2 con nuestro sistema de 6 pasos claros.
              </p>
              <Button 
                onClick={createCase}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg shadow-emerald-500/25"
                data-testid="create-case-btn"
              >
                Comenzar mi Camino al Éxito
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          ) : caseData.service_status === "completed" ? (
            // Service Completed - Show limited dashboard
            <ServiceCompletedDashboard caseData={caseData} user={user} />
          ) : (
            // Has Case - Dashboard with Camino al Éxito
            <div className="space-y-6">
              {/* Welcome Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 shadow-xl overflow-hidden relative"
              >
                {/* Background pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:32px_32px]" />
                
                <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-emerald-400 font-medium mb-1">Hola, {user?.name}</p>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
                      Camino al Éxito: {caseData?.progress_percentage || 0}%
                    </h1>
                    <p className="text-navy-300">
                      Etapa {caseData?.current_step || 1} de 6: {caseData?.steps?.find(s => s.step_order === caseData?.current_step)?.step_name || 'Inicio'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full border border-emerald-500/30">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      <span className="text-emerald-400 font-medium text-sm">Caso activo</span>
                    </div>
                  </div>
                </div>
                <div className="relative mt-6">
                  <div className="flex justify-between text-sm text-navy-300 mb-2">
                    <span>Progreso general</span>
                    <span>{caseData?.progress_percentage || 0}%</span>
                  </div>
                  <Progress value={caseData?.progress_percentage || 0} className="h-3 bg-navy-700" />
                </div>
              </motion.div>

              {/* Next Step Banner - Action-oriented guidance */}
              <NextStepBanner 
                caseData={caseData} 
                ds160Progress={ds160Progress} 
                user={user}
                documentsCount={documentsCount}
              />

              {/* Interview Countdown - Si tiene cita programada */}
              {caseData.interview_date && (
                <InterviewCountdown 
                  interviewDate={caseData.interview_date}
                  caseData={caseData}
                />
              )}

              {/* Upcoming Meeting Banner - Prominent Zoom meeting reminder */}
              <UpcomingMeetingBanner />

              {/* Full Width Layout - No sidebar */}
              <div className="space-y-6">
                  {/* Camino al Éxito Visual */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <CaminoAlExito 
                  steps={caseData.steps || []} 
                  currentStep={caseData.current_step}
                  onStepClick={handleStepClick}
                  documentsCount={documentsCount}
                  ds160Progress={ds160Progress}
                  isTrialMode={false}
                />
              </motion.div>

              {/* Stats Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-navy-500 text-sm">Probabilidad</span>
                    <TrendingUp className={`w-5 h-5 ${caseData.probability_score > 0 ? "text-emerald-500" : "text-navy-300"}`} />
                  </div>
                  <p className="font-heading text-3xl font-bold text-navy-900">
                    {caseData.probability_score > 0 ? `${caseData.probability_score}%` : "-"}
                  </p>
                  <p className="text-sm text-navy-500 mt-1">
                    {caseData.probability_score > 0 ? "de aprobación estimada" : "completa pasos para calcular"}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-navy-500 text-sm">Nivel de Riesgo</span>
                    {(() => {
                      const badge = getRiskBadge(caseData.risk_level);
                      const Icon = badge.icon;
                      return <Icon className={`w-5 h-5 ${
                        !caseData.risk_level ? "text-navy-300" :
                        caseData.risk_level === "low" ? "text-emerald-500" :
                        caseData.risk_level === "medium" ? "text-amber-500" : "text-red-500"
                      }`} />;
                    })()}
                  </div>
                  <div className={getRiskBadge(caseData.risk_level).class}>
                    {getRiskBadge(caseData.risk_level).text}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-navy-500 text-sm">Optimización</span>
                    <Zap className={`w-5 h-5 ${caseData.monitoring_active ? "text-purple-500" : "text-navy-400"}`} />
                  </div>
                  <p className={`font-semibold ${caseData.monitoring_active ? "text-purple-600" : "text-navy-500"}`}>
                    {caseData.monitoring_active ? "Activa 24/7" : "No activada"}
                  </p>
                  <p className="text-sm text-navy-500 mt-1">IA + BOT + Humanos</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-navy-500 text-sm">Tipo de Visa</span>
                    <FileText className="w-5 h-5 text-gold-600" />
                  </div>
                  <p className="font-heading text-2xl font-bold text-navy-900">
                    {caseData.visa_type}
                  </p>
                  <p className="text-sm text-navy-500 mt-1">Turista/Negocios</p>
                </motion.div>
              </div>

              {/* Risk Analysis Card */}
              {caseData.questionnaire_data && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 }}
                >
                  <RiskAnalysisCard caseId={caseData.id} compact={true} />
                </motion.div>
              )}

              {/* Approval Boost Card - Show suggestions to increase probability */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.58 }}
              >
                <ApprovalBoostCard 
                  hasNotarizedInvitation={caseData.questionnaire_data?.has_notarized_invitation || false}
                  onUpdate={async (field, value) => {
                    try {
                      const token = localStorage.getItem("token");
                      const updatedQuestionnaire = {
                        ...caseData.questionnaire_data,
                        [field]: value
                      };
                      await axios.put(
                        `${API}/cases/${caseData.id}/questionnaire`,
                        updatedQuestionnaire,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setCaseData(prev => ({
                        ...prev,
                        questionnaire_data: updatedQuestionnaire
                      }));
                      toast.success("¡Excelente! Tu probabilidad de aprobación ha aumentado");
                    } catch (error) {
                      toast.error("Error al actualizar");
                    }
                  }}
                  compact={true}
                />
              </motion.div>

              {/* Interview Date if set */}
              {caseData.interview_date && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="card-elevated p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-blue-600 font-medium text-sm">FECHA DE ENTREVISTA</p>
                      <p className="font-heading text-2xl font-bold text-navy-900">{caseData.interview_date}</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Quick Actions */}
              <div className="grid sm:grid-cols-3 gap-4">
                <Link to="/interview-questions">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.75 }}
                    className="card-elevated p-6 hover:border-purple-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <HelpCircle className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-900">Preguntas de Entrevista</h3>
                        <p className="text-sm text-navy-500">Por consulado</p>
                      </div>
                    </div>
                  </motion.div>
                </Link>

                <Link to="/help">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="card-elevated p-6 hover:border-gold-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-900">Centro de Ayuda IA</h3>
                        <p className="text-sm text-navy-500">Asistente disponible 24/7</p>
                      </div>
                    </div>
                  </motion.div>
                </Link>

                <Link to="/notifications">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    className="card-elevated p-6 hover:border-gold-300 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Bell className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-900">Notificaciones</h3>
                        <p className="text-sm text-navy-500">
                          {unreadCount > 0 ? `${unreadCount} sin leer` : "Todo al día"}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </div>

              {/* Consulate Directory Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.92 }}
              >
                <ConsulateDirectory />
              </motion.div>

              {/* Plan Section with Travel Promotion */}
              <PlanSection />

              {/* Family Members Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <FamilyMembersManager />
              </motion.div>

              {/* DS-160 Authorization Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95 }}
              >
                <DS160AuthorizationStatus />
              </motion.div>

              {/* Recent Notifications */}
              {notifications.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="card-elevated p-6"
                >
                  <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
                    Actividad Reciente
                  </h3>
                  <div className="space-y-3">
                    {notifications.slice(0, 3).map((notif) => (
                      <div key={notif.id} className={`flex items-start gap-3 p-3 rounded-lg ${
                        !notif.read ? "bg-gold-50" : "bg-navy-50"
                      }`}>
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          !notif.read ? "bg-gold-500" : "bg-navy-300"
                        }`}></div>
                        <div>
                          <p className="text-navy-700">{notif.message}</p>
                          <p className="text-xs text-navy-400 mt-1">
                            {new Date(notif.sent_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Chat Button - Hide when service is completed */}
      {caseData?.service_status !== "completed" && <ChatButton />}

      {/* WhatsApp Bubble para soporte al cliente */}
      <WhatsAppBubble 
        userName={user?.name || "Cliente"} 
        userEmail={user?.email || ""} 
      />

      {/* Quick Start Guide - Floating button + modal */}
      {caseData && caseData.service_status !== "completed" && (
        <QuickStartGuide 
          currentStep={caseData?.current_step || 1}
          documentsCount={documentsCount}
          ds160Progress={ds160Progress}
        />
      )}

      {/* WhatsApp Support Button - Floating */}
      <WhatsAppSupportButton 
        caseId={caseData?.id}
        clientName={user?.name}
      />
    </div>
  );
};

// Wrap Dashboard with ErrorBoundary
import ErrorBoundary from "../components/ErrorBoundary";

const DashboardWithErrorBoundary = () => (
  <ErrorBoundary>
    <Dashboard />
  </ErrorBoundary>
);

export default DashboardWithErrorBoundary;
